// src/components/instructor/OrgTeamView.js
//
// Team management view — instructor grid with SAS school / Collective switcher.
// Loads members + bookings for earnings and availability data.

import React, { useState, useMemo } from 'react';
import { buildFinanceData } from '../../utils/financeData';
import OrgRosterReview from './OrgRosterReview';
import OrgTeamSeasons from './OrgTeamSeasons';
import StaffTimesheetBoard from './StaffTimesheetBoard';
import TeamMemberOpsControls from './ops/TeamMemberOpsControls';
import { useAuth } from '../../AuthContext';
import { assertOrgWriteAuthorized } from '../../utils/operationalWriteGate';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { CURRENT_SEASON_ID } from '../../utils/instructorCategory';
import { memberOperatingAreaIds, areaKeyOf } from '../../utils/operatingAreas';

const V = {
  surface: '#FFFFFF', s2: '#F2F1EE', s3: '#ECEAE6',
  border: '#E2E0DB', borderStrong: '#C8C5BE',
  t1: '#1A1917', t2: '#6B6860', t3: '#9E9B95',
  green: '#1D9E75', greenBg: '#E1F5EE', greenTxt: '#085041',
  amber: '#C97D10', amberBg: '#FEF3C7', amberTxt: '#92400E',
  blue: '#2563EB', blueBg: '#EFF4FE', blueTxt: '#1A3FA0',
  purple: '#7C3AED', purpleBg: '#F5F3FF', purpleSoft: '#EDE9FE', purpleTxt: '#4C1D95',
  grayBg: '#F4F3F0', grayTxt: '#57534E',
  shadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
};
const F = "'DM Sans',sans-serif";
const FM = "'DM Mono',monospace";

// Pass 49A §review-cleanup: resolve the organizationMembers doc id for a roster write from
// member.id, else the deterministic `${userId}::${organizationId}`. NEVER fall back to a
// hard-coded org — refuse (throw) rather than risk a cross-tenant write.
export function resolveMembershipDocId(member = {}) {
  if (member.id) return member.id;
  if (member.userId && member.organizationId) return `${member.userId}::${member.organizationId}`;
  throw new Error('Cannot save roster: member has no id and no organizationId to resolve a membership document.');
}

const EMPTY = [];
function OrgTeamView({ bookings: bookingsProp, members: membersProp }) {
  const bookings = bookingsProp || EMPTY;
  const members = membersProp || EMPTY;
  const loading = bookingsProp === null || membersProp === null;
  const { userData } = useAuth() || {};
  // Pass 57A: org id for the schedule-visibility admin control (canonical org of this team).
  const orgId = (members.find((m) => m.organizationId) || {}).organizationId || '';

  const [pool, setPool] = useState('internal');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('directory'); // 'directory' | 'team' | 'roster'

  // Pass 49A §8/§10: roster save writes canonical seasonMemberships.participation as the
  // PRIMARY current-team signal (category stays a SEPARATE field). No notification.
  const saveRoster = async (member, patch) => {
    // Pass 57C (item 13): roster/category changes go through the SAME normalized authorization as every
    // other org write (owner/admin/ops + org scope). No more ungated direct updateDoc.
    assertOrgWriteAuthorized({ userData, organizationId: member.organizationId || orgId, action: 'team_access' });
    // Pass 49A §review-cleanup: NEVER fall back to a hard-coded org. Resolve the membership
    // doc from member.id, or the deterministic `${userId}::${organizationId}` — and refuse
    // the write if neither the id nor a trustworthy organizationId is available (do not risk
    // writing across the multi-tenant boundary).
    const ref = doc(db, 'organizationMembers', resolveMembershipDocId(member));
    const upd = {};
    if (patch.participation !== undefined) {
      upd[`seasonMemberships.${CURRENT_SEASON_ID}.participation`] = patch.participation; // 'current'|'not_returning'|'unknown'
    }
    if (patch.category !== undefined) upd[`seasonMemberships.${CURRENT_SEASON_ID}.category`] = patch.category || '';
    upd[`seasonMemberships.${CURRENT_SEASON_ID}.source`] = 'ops roster review';
    upd.rosterUpdatedAt = new Date();
    await updateDoc(ref, upd);
  };
  const [expandedId, setExpandedId] = useState(null);

  const finData = useMemo(() => buildFinanceData(bookings, members), [bookings, members]);
  const { instructors } = finData;

  // Pass 46E: three explicit buckets — unclassified is NEVER folded into School.
  const internalCount = instructors.filter(i => i.category === 'school').length;
  const collectiveCount = instructors.filter(i => i.category === 'collective').length;
  const needsReviewCount = instructors.filter(i => i.isUnclassified).length;

  // Pass 47: current-season (26/27) team state. A 25/26 member is NOT automatically
  // current — until the 26/27 roster is sourced, they are "needs roster review".
  const confirmedCurrent = instructors.filter(i => i.teamState === 'confirmed_current').length;
  const rosterReview = instructors.filter(i => i.teamState === 'needs_roster_review').length;
  const rosterUnestablished = confirmedCurrent === 0 && rosterReview > 0;

  const filtered = instructors.filter(i => {
    if (pool === 'internal' && i.category !== 'school') return false;
    if (pool === 'collective' && i.category !== 'collective') return false;
    if (pool === 'needsReview' && !i.isUnclassified) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!i.name.toLowerCase().includes(q) && !i.discipline.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Weekly availability
  const weekAvail = useMemo(() => {
    const today = new Date();
    const monday = new Date(today);
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
    monday.setHours(0, 0, 0, 0);
    const map = {};
    for (const inst of instructors) map[inst.userId] = [false, false, false, false, false, false, false];
    for (const b of bookings) {
      if (b.status === 'canceled' || !b.instructorId) continue;
      const s = b.startAt?.toDate ? b.startAt.toDate() : b.startAt?.seconds ? new Date(b.startAt.seconds * 1000) : new Date(b.startAt);
      if (!s) continue;
      const diff = Math.floor((s - monday) / 86400000);
      if (diff >= 0 && diff < 7 && map[b.instructorId]) map[b.instructorId][diff] = true;
    }
    return map;
  }, [instructors, bookings]);

  if (loading) return <div style={{ padding: '60px 0', textAlign: 'center', color: V.t3, fontSize: 13, fontFamily: F }}>Loading team data...</div>;

  return (
    <div style={{ fontFamily: F }}>
      {/* Pass 58F6 — the ORGANISATION-level schedule visibility policy moved to its canonical home,
          Settings. The per-member publication override remains in Team → member ops controls. */}
      {/* Pass 58G1 — Directory (longitudinal roster) is the ONLY primary Team surface. "Stats" (financeData)
          stays hidden. Roster review is internal data-cleanup — DEMOTED to a restrained secondary "Team tools"
          link (owner/admin/ops per existing permissions), never a peer tab beside Directory. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 2, background: V.s2, border: `1px solid ${V.border}`, borderRadius: 8, padding: 2, width: 'fit-content' }}>
          <button onClick={() => setView('directory')} style={{ fontFamily: F, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: view === 'directory' ? V.surface : 'transparent', color: view === 'directory' ? V.t1 : V.t3, boxShadow: view === 'directory' ? V.shadow : 'none' }}>Directory</button>
          {/* Pass 83C — canonical staff-timesheet completion board (delivery-truth, NOT the hidden Finance projection). */}
          <button data-testid="team-timesheets" onClick={() => setView('timesheets')} style={{ fontFamily: F, fontSize: 12.5, fontWeight: 700, padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: view === 'timesheets' ? V.surface : 'transparent', color: view === 'timesheets' ? V.t1 : V.t3, boxShadow: view === 'timesheets' ? V.shadow : 'none' }}>Timesheets</button>
        </div>
        <div style={{ flex: 1 }} />
        {rosterReview ? (
          <button data-testid="team-tools-roster" onClick={() => setView('roster')} style={{ fontFamily: F, fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7, border: `1px solid ${V.border}`, background: view === 'roster' ? V.surface : 'transparent', color: V.t3, cursor: 'pointer' }}>Team tools · Review historical roster ({rosterReview})</button>
        ) : null}
      </div>
      {view === 'directory' ? <OrgTeamSeasons members={members} bookings={bookings} />
        : view === 'timesheets' ? <StaffTimesheetBoard members={members} organizationId={orgId} />
        : view === 'roster' ? <OrgRosterReview members={members} onSaveRoster={saveRoster} /> : <>
      {/* Pass 47: 26/27 roster not yet established — do not imply these are confirmed current staff. */}
      {rosterUnestablished && (
        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: '#92400E' }}>
          <strong>2026/27 roster not yet established.</strong> {rosterReview} people worked a previous season and need roster confirmation for the current season
          (last-known: {internalCount} School · {collectiveCount} Collective). These are shown for review, not as confirmed current staff.
        </div>
      )}
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
        <StatCard label="Total instructors" value={instructors.length} sub={`${internalCount} school · ${collectiveCount} collective${needsReviewCount ? ` · ${needsReviewCount} needs review` : ''}`} />
        <StatCard label="SAS school" value={internalCount} sub="hourly · admin-assigned" color={V.blue} />
        <StatCard label="Collective" value={collectiveCount} sub="own schedule · own clients" color={V.purple} />
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12 }}>
        <div style={{ display: 'flex', gap: 2, background: V.surface, border: `1px solid ${V.border}`, borderRadius: 8, padding: 2 }}>
          {[{ key: 'internal', label: 'SAS school' }, { key: 'collective', label: 'Collective' }, ...(needsReviewCount ? [{ key: 'needsReview', label: `Needs review (${needsReviewCount})` }] : [])].map(p => (
            <button key={p.key} onClick={() => { setPool(p.key); setExpandedId(null); }} style={{
              padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: pool === p.key ? 600 : 400,
              color: pool === p.key ? '#fff' : V.t3, border: 'none',
              background: pool === p.key ? (p.key === 'collective' ? V.purple : V.t1) : 'none',
              fontFamily: F, cursor: 'pointer',
            }}>{p.label}</button>
          ))}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or discipline…"
          style={{ fontFamily: F, fontSize: 12, padding: '6px 12px', border: `1px solid ${V.borderStrong}`, borderRadius: 8, background: V.surface, outline: 'none', width: 220, color: V.t1 }} />
      </div>

      {/* Collective info bar */}
      {pool === 'collective' && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: V.surface, border: `1px solid ${V.purpleSoft}`, borderRadius: 12, display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: V.purpleTxt, marginBottom: 6 }}>Commission to SAS</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[{ rate: '25%', label: 'Stage 1', bg: V.amberBg, c: V.amberTxt }, { rate: '20%', label: 'Stage 2', bg: V.blueBg, c: V.blueTxt }, { rate: '18%', label: 'Stage 3', bg: V.greenBg, c: V.greenTxt }].map(s => (
                <div key={s.label} style={{ textAlign: 'center', padding: '6px 12px', background: s.bg, borderRadius: 8 }}>
                  <div style={{ fontSize: 16, fontWeight: 600, fontFamily: FM, color: s.c }}>{s.rate}</div>
                  <div style={{ fontSize: 10, color: s.c, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ fontSize: 11, color: V.t2, maxWidth: 340, lineHeight: 1.6 }}>
            Commission applied after consumption tax (÷11) and card processing (3.6%). Passed work earns originating instructor 10%.
          </div>
        </div>
      )}

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {filtered.map(inst => {
          const isCol = pool === 'collective';
          const initials = inst.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
          const discTags = [];
          const d = (inst.discipline || '').toLowerCase();
          if (d.includes('ski')) discTags.push('Ski');
          if (d.includes('sb') || d.includes('snowboard')) discTags.push('Snowboard');
          const expanded = expandedId === inst.userId;
          const wa = weekAvail[inst.userId];
          // Pass 83A §42 — show the OPERATING AREA relationship (distinct from commercial category) + a TEST badge.
          const rawM = members.find((m) => m.userId === inst.userId) || {};
          const areaLabels = memberOperatingAreaIds(rawM, CURRENT_SEASON_ID).map((id) => { const k = areaKeyOf(id); return k ? k.charAt(0).toUpperCase() + k.slice(1) : ''; }).filter(Boolean);
          const isTestMember = rawM.testAccount === true;

          return (
            <div key={inst.userId} style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, boxShadow: V.shadow, overflow: 'hidden', borderLeft: `3px solid ${isCol ? V.purple : V.blue}` }}>
              <div onClick={() => setExpandedId(expanded ? null : inst.userId)} style={{ padding: 14, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = V.s2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: isCol ? `linear-gradient(135deg,${V.purple},#8B5CF6)` : `linear-gradient(135deg,${V.blue},#6366F1)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#fff', flexShrink: 0 }}>{initials}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: V.t1 }}>{inst.name}</div>
                    <div style={{ fontSize: 11, color: V.t3, fontFamily: FM, marginTop: 1 }}>{inst.discipline}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                  {discTags.map(t => <span key={t} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 500, background: t === 'Ski' ? V.blueBg : V.purpleSoft, color: t === 'Ski' ? V.blueTxt : V.purpleTxt }}>{t}</span>)}
                  {/* Pass 83A — operating area(s): a SEPARATE axis from commercial category. */}
                  {areaLabels.map(a => <span key={a} data-testid={`team-area-${inst.userId}-${a}`} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 600, background: '#EAF2FF', color: '#1A3FA0' }}>{a}</span>)}
                  {isTestMember && <span data-testid="team-test-badge" style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700, background: '#FEE2E2', color: '#B91C1C' }}>TEST</span>}
                  {/* Pass 58D — rate/¥ earnings hidden for V1 (no authoritative payroll source yet). */}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 12, color: V.t3, fontFamily: FM }}>{inst.seasonLessons > 0 ? `${inst.seasonLessons} sessions` : '—'}</div>
                  {wa && (
                    <div style={{ display: 'flex', gap: 2 }} title="This week: Mon–Sun">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((label, i) => (
                        <div key={i} style={{ width: 10, height: 10, borderRadius: 2, background: wa[i] ? V.green : V.s3, border: `1px solid ${wa[i] ? V.green : V.border}` }} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {expanded && (
                <div style={{ borderTop: `1px solid ${V.border}`, padding: 14, background: V.s2 }}>
                  {/* Pass 58D — HIDE UNTIL REAL: per-month ¥ earnings + season ¥ total removed for V1 (no
                      authoritative payroll/earnings source). The real operational workload counts stay. */}
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: V.t2, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span>{isCol ? `${inst.seasonFD} FD · ${inst.seasonHD} HD` : `${Math.round(inst.seasonHours)}h · ${inst.seasonLessons} sessions`}</span>
                  </div>
                  {/* Pass 57C: live per-member control centre — category, tri-state capabilities, calendar visibility. */}
                  <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${V.border}` }}>
                    <TeamMemberOpsControls member={members.find((m) => m.userId === inst.userId) || inst} organizationId={orgId} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ gridColumn: '1/-1', padding: 24, textAlign: 'center', color: V.t3, fontSize: 12 }}>No instructors found</div>}
      </div>
      </>}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ background: V.surface, border: `1px solid ${V.border}`, borderRadius: 12, padding: '14px 16px', boxShadow: V.shadow }}>
      <div style={{ fontSize: 10, color: V.t3, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, color: color || V.t1, lineHeight: 1, letterSpacing: '-0.03em', fontFamily: FM }}>{value}</div>
      <div style={{ fontSize: 11, color: V.t3, marginTop: 4 }}>{sub}</div>
    </div>
  );
}

export default OrgTeamView;
